import { createSelector } from '@reduxjs/toolkit';
import { DEFAULT_LANGUAGE } from '../shared/languages';
import type { CmsIncludedProduct } from '../types/productCms';
import { selectLanguage } from './routingSelectors';
import type { RootState } from './store';

export interface CartProductView {
  ratePlan: string;
  eyebrow: string;
  name: string;
  logo: string;
  benefits: string[];
  /** Catalog billing period (e.g. "Annual"), used for the price note. */
  billingPeriod: string;
  /** Catalog license type (e.g. "fixed_seat", "floating"), used for the price note. */
  licenseType: string;
  quantity: number;
  /** Smallest quantity the catalog allows for this item. */
  minAmt: number;
  /** Largest quantity the catalog allows, or undefined when uncapped. */
  maxAmt?: number;
  price: number;
  hasData: boolean;
  upgrade: {
    eyebrow: string;
    name: string;
    includes: string[];
    logos: CmsIncludedProduct[];
    /** The upgrade's own rate plan (CmsUpgradeInfo.ratePlanID), used to swap into it. */
    ratePlan: string;
  } | null;
}

export interface PreviewTotals {
  subtotal: number;
  taxes: number;
  total: number;
}

export interface PromoDiscount {
  name: string;
  amount: number;
}

export const selectPromoCode = (state: RootState) => state.cart.promoCode;

/** The promo code the cart currently carries on the server, if any. */
export const selectAppliedPromoCode = (state: RootState) =>
  state.cart.persistentCart?.promoCode ?? '';

/** Whether the current preview confirms the promo code is applied. */
export const selectPromoApplied = (state: RootState) =>
  state.cart.productsPreview?.promoStatus === 'Applied';

/** Whether the current preview says the entered promo code doesn't apply. */
export const selectPromoInvalid = (state: RootState) => {
  const status = state.cart.productsPreview?.promoStatus;
  return status === 'Invalid' || status === 'NotApplicable';
};

/** Whether the initial `init()` data load is still in flight. */
export const selectCartLoading = (state: RootState) => state.cart.loading;

/** Whether the initial `init()` data load has completed at least once. */
export const selectCartInitialized = (state: RootState) =>
  state.cart.initialized;

const selectPersistentCart = (state: RootState) => state.cart.persistentCart;
const selectProductsCmsData = (state: RootState) => state.cart.productsCmsData;
const selectCatalog = (state: RootState) => state.cart.catalog;
const selectProductsPreview = (state: RootState) => state.cart.productsPreview;

export const selectCartProducts = createSelector(
  [
    selectPersistentCart,
    selectProductsCmsData,
    selectCatalog,
    selectProductsPreview,
    selectLanguage,
  ],
  (persistentCart, cmsData, catalog, preview, language): CartProductView[] => {
    if (!persistentCart) return [];

    return persistentCart.products.map((cartProduct) => {
      const cms = cmsData?.find((c) => c.ratePlanId === cartProduct.ratePlan);
      const description =
        cms?.descriptions[language] ?? cms?.descriptions[DEFAULT_LANGUAGE];
      const product = description?.product;
      const upgrade = description?.upgrade;

      const catalogItem = catalog?.items.find(
        (item) => item.ratePlanId === cartProduct.ratePlan,
      );
      const invoiceItem = preview?.invoiceItems?.find(
        (item) => item.productRatePlanId === cartProduct.ratePlan,
      );

      const hasData = Boolean(description && catalogItem && invoiceItem);

      return {
        ratePlan: cartProduct.ratePlan,
        eyebrow: product?.headline ?? '',
        name: product?.name ?? '',
        logo: product?.icon ?? '',
        benefits: product?.includes ?? [],
        billingPeriod: catalogItem?.billingPeriod ?? '',
        licenseType: catalogItem?.licenseType ?? '',
        quantity: cartProduct.quantity,
        minAmt: catalogItem?.minAmt || 1,
        maxAmt: catalogItem?.maxAmt || undefined,
        price: invoiceItem?.chargeAmount ?? 0,
        hasData,
        upgrade: upgrade
          ? {
              eyebrow: upgrade.headline ?? '',
              name: upgrade.name ?? '',
              includes: upgrade.includes ?? [],
              logos: upgrade.includedProducts ?? [],
              ratePlan: upgrade.ratePlanID ?? '',
            }
          : null,
      };
    });
  },
);

export const selectPreviewTotals = createSelector(
  [selectProductsPreview],
  (preview): PreviewTotals => ({
    subtotal: preview?.amountWithoutTax ?? 0,
    taxes: preview?.taxAmount ?? 0,
    total: preview?.amount ?? 0,
  }),
);

/** The applied promo's discount line, named and totalled like the invoice. */
export const selectPromoDiscount = createSelector(
  [selectProductsPreview],
  (preview): PromoDiscount | null => {
    const list = preview?.promoItems?.list ?? [];
    if (list.length === 0) return null;

    const amount = list.reduce((sum, item) => sum + item.chargeAmount, 0);
    return { name: list[0].chargeName, amount };
  },
);
